import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Lock, Mail, Check, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';

const AdminProfilePage = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('admin_token');
  const [adminData, setAdminData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  
  // Password change form
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);

  useEffect(() => {
    if (!token) {
      navigate('/admin/login');
      return;
    }
    
    // Load admin data from localStorage
    const stored = localStorage.getItem('admin_data');
    if (stored) {
      setAdminData(JSON.parse(stored));
    }
  }, [token, navigate]);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    // Validation
    if (newPassword.length < 8) {
      setError('Neues Passwort muss mindestens 8 Zeichen haben');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setError('Passwörter stimmen nicht überein');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/admin/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Fehler beim Ändern des Passworts');
      }
      
      setSuccess('Passwort erfolgreich geändert!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/admin/dashboard')}
              className="p-2"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Admin Profil</h1>
              <p className="text-sm text-gray-500">Profileinstellungen und Passwort ändern</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Profile Info */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 bg-brand-green/10 rounded-full flex items-center justify-center">
              <User className="w-8 h-8 text-brand-green" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">{adminData?.name || 'Admin'}</h2>
              <p className="text-gray-500 flex items-center gap-2">
                <Mail className="w-4 h-4" />
                {adminData?.email || 'admin@example.com'}
              </p>
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="text-gray-500 mb-1">Admin ID</div>
              <div className="font-mono text-gray-900">{adminData?.admin_id?.slice(0, 8)}...</div>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="text-gray-500 mb-1">Rolle</div>
              <div className="font-semibold text-brand-green">Administrator</div>
            </div>
          </div>
        </div>

        {/* Change Password */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
              <Lock className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Passwort ändern</h3>
              <p className="text-sm text-gray-500">Aktualisieren Sie Ihr Admin-Passwort</p>
            </div>
          </div>

          {success && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl flex items-center gap-3">
              <Check className="w-5 h-5 text-green-600" />
              <p className="text-green-700">{success}</p>
            </div>
          )}

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-600" />
              <p className="text-red-700">{error}</p>
            </div>
          )}

          <form onSubmit={handleChangePassword} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                Aktuelles Passwort
              </label>
              <div className="relative">
                <input
                  type={showPasswords ? 'text' : 'password'}
                  required
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-green pr-12"
                  placeholder="Ihr aktuelles Passwort"
                  data-testid="current-password-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                Neues Passwort
              </label>
              <div className="relative">
                <input
                  type={showPasswords ? 'text' : 'password'}
                  required
                  minLength={8}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-green pr-12"
                  placeholder="Mindestens 8 Zeichen"
                  data-testid="new-password-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                Neues Passwort bestätigen
              </label>
              <div className="relative">
                <input
                  type={showPasswords ? 'text' : 'password'}
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-green pr-12"
                  placeholder="Passwort wiederholen"
                  data-testid="confirm-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords(!showPasswords)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPasswords ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Password requirements */}
            <div className="bg-gray-50 rounded-xl p-4 text-sm">
              <div className="font-medium text-gray-700 mb-2">Passwort-Anforderungen:</div>
              <ul className="space-y-1">
                <li className={`flex items-center gap-2 ${newPassword.length >= 8 ? 'text-green-600' : 'text-gray-500'}`}>
                  <Check className={`w-4 h-4 ${newPassword.length >= 8 ? 'text-green-500' : 'text-gray-300'}`} />
                  Mindestens 8 Zeichen
                </li>
                <li className={`flex items-center gap-2 ${newPassword === confirmPassword && newPassword.length > 0 ? 'text-green-600' : 'text-gray-500'}`}>
                  <Check className={`w-4 h-4 ${newPassword === confirmPassword && newPassword.length > 0 ? 'text-green-500' : 'text-gray-300'}`} />
                  Passwörter stimmen überein
                </li>
              </ul>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-brand-green hover:bg-brand-green-dark text-white py-3 rounded-xl font-semibold"
              data-testid="change-password-btn"
            >
              {loading ? 'Wird geändert...' : 'Passwort ändern'}
            </Button>
          </form>
        </div>
      </main>
    </div>
  );
};

export default AdminProfilePage;
